// src/features/home/pages/HomePage.jsx
import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { tasksAPI } from '../../tasks/services/tasks';
import { useAuth } from '../../../context/AuthContext';
import { usePermissions } from '../../auth/hooks/usePermissions';
import ExpiryPanel from '../components/ExpiryPanel';
import ApprovalsPanel from '../components/ApprovalsPanel';
import ClockInOutWidget from '../components/ClockInOutWidget';
import MyTasksTable from '../components/MyTasksTable';
import CategorySummaryList from '../components/CategorySummaryList';
import { capitalizeFirst } from '../../../utils/formatters/capitalize';

function getGreetingKey() {
  const hour = new Date().getHours();
  if (hour < 12) return 'home:greeting.morning';
  if (hour < 18) return 'home:greeting.afternoon';
  return 'home:greeting.evening';
}

export default function HomePage() {
  const { t } = useTranslation(['home']);
  const { user } = useAuth();
  const { hasScope, hasAnyScope } = usePermissions();
  const canReadTasks = hasScope('task:read');
  // The expiry panel lists safety data sheets, batches and delegations
  const canSeeExpiries = hasAnyScope(['inventory:msds:read', 'inventory:lot:read', 'delegation:read']);

  const [myTasks, setMyTasks] = useState([]);
  const [categorySummary, setCategorySummary] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(true);

  const loadTasks = useCallback(async () => {
    if (!user?.staff_member_id || !canReadTasks) return;

    setLoadingTasks(true);
    try {
      const result = await tasksAPI.getHomeTasks();
      if (result.data) {
        setMyTasks(result.data.my_tasks?.tasks || []);
        setCategorySummary(result.data.category_summary?.categories || []);
      }
    } catch (error) {
      console.error('Error loading tasks:', error);
    } finally {
      setLoadingTasks(false);
    }
  }, [user?.staff_member_id, canReadTasks]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const firstName = user?.name?.split(' ')[0] || user?.name;

  return (
    <Box sx={{ display: 'flex', gap: 6, p: { xs: 4, md: 8 }, width: '100%', maxWidth: 1600, mx: 'auto', boxSizing: 'border-box' }}>

      {/* Main content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="h3" fontWeight={700} sx={{ mb: 1 }}>
          {t(getGreetingKey(), { name: capitalizeFirst(firstName) })}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 6 }}>
          {t('home:subtitle')}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {canReadTasks && (
            <>
              <MyTasksTable tasks={myTasks} loading={loadingTasks} />
              <CategorySummaryList categories={categorySummary} loading={loadingTasks} />
            </>
          )}
        </Box>
      </Box>

      {/* Right widget sidebar */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          gap: 4,
          width: 340,
          flexShrink: 0,
        }}
      >
        <ClockInOutWidget staffMemberId={user?.staff_member_id} departmentId={user?.department_id} />
        {canSeeExpiries && <ExpiryPanel />}
        <ApprovalsPanel />
      </Box>

    </Box>
  );
}
